/**
 * Copy the one name / phone pair the customer fills in
 * at checkout into the `whatsapp_*` pair the
 * notification pipeline reads, so nobody types the
 * same thing twice.
 *
 * The direction reversed in CAL-420. The checkout used
 * to ask for an optional WhatsApp name and phone at
 * the top and the validated delivery pair further
 * down, and mirrored the first into the second. The
 * redesign shows ONE pair, bound to the validated
 * field, and the `whatsapp_*` pair is now hidden
 * inputs fed from it.
 *
 * Rules:
 *   - Mirror on connect (server-rendered values) and
 *     on every keystroke in the source field.
 *   - Once the user has typed directly into a target,
 *     treat that field as user-owned and stop
 *     mirroring into it. Tracked via a
 *     `data-user-edited` flag on the target, set by an
 *     explicit input action wired on the target
 *     itself. Hidden targets never receive input, so
 *     in the checkout's direction the flag never
 *     fires.
 *
 * Usage:
 * ```html
 * <form data-controller="whatsapp-mirror">
 *   <input data-whatsapp-mirror-target="sourceName"
 *          data-action="input->whatsapp-mirror#mirror">
 *   <input data-whatsapp-mirror-target="sourcePhone"
 *          data-action="input->whatsapp-mirror#mirror">
 *   ...
 *   <input data-whatsapp-mirror-target="targetName"
 *          data-action="input->whatsapp-mirror#markEdited">
 *   <input data-whatsapp-mirror-target="targetPhone"
 *          data-action="input->whatsapp-mirror#markEdited">
 * </form>
 * ```
 */
import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "sourceName",
    "sourcePhone",
    "targetName",
    "targetPhone"
  ];

  // Server-rendered values (e.g. logged-in user's
  // saved name/phone) need to flow into the
  // delivery fields too: `input` actions only fire
  // on user keystrokes.
  connect() {
    this.mirror();
  }

  mirror() {
    this.copyIfEditable("sourceName", "targetName");
    this.copyIfEditable("sourcePhone", "targetPhone");
  }

  // Action wired on the target inputs. Marks them as
  // user-owned the moment the user types directly,
  // so subsequent source-keystroke mirrors leave
  // them alone.
  markEdited(event) {
    if (!event || !event.target) return;
    event.target.dataset.userEdited = "true";
  }

  copyIfEditable(sourceKey, targetKey) {
    const sourceTarget = `has${capitalize(
      sourceKey
    )}Target`;
    const targetTarget = `has${capitalize(
      targetKey
    )}Target`;
    if (!this[sourceTarget] || !this[targetTarget]) {
      return;
    }
    const source = this[`${sourceKey}Target`];
    const target = this[`${targetKey}Target`];
    if (target.dataset.userEdited === "true") return;
    if (target.value === source.value) return;
    target.value = source.value;
    target.dispatchEvent(
      new Event("change", { bubbles: true })
    );
  }
}

function capitalize(s) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
